import { useInfiniteQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { fetchLayoutType } from 'src/hooks';
import { IAssemblyListing } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const useGetScheduleDefaultAssemblies = (filters: string[] = []) => {
  return useInfiniteQuery({
    queryKey: queryKeys.assemblies.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchScheduleDefaultAssemblies(pageParam, filters),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

const fetchScheduleDefaultAssemblies = async (
  page: number,
  filters: string[] = [],
): Promise<{
  assemblies: IAssemblyListing[];
  totalPages: number;
  totalCount: number;
}> => {
  const query = concat(filters, ['count=true', `page=${page}`]);
  const url = `/scheduleDefaultAssemblyList${generateQuery(
    ['layouts'],
    query,
  )}`;

  const [response, layoutTypes] = await Promise.all([
    requestCCApi({ url }),
    queryClient.fetchQuery({
      queryKey: queryKeys.layoutTypes.all(),
      queryFn: fetchLayoutType,
      staleTime: Infinity,
    }),
  ]);

  const layoutTypeIdFilter = filters.find((filter) =>
    filter.startsWith('layoutTypeId='),
  ) as string;
  const layoutTypeId = parseInt(layoutTypeIdFilter.split('=')[1], 10);

  // Find the layout type name, assuming a match will always be found
  const layoutTypeName = layoutTypes.find(
    (lt: { id: number; name: string }) => lt.id === layoutTypeId,
  )!.name;

  const assembliesWithLayoutName = response.data.map((assembly: any) => ({
    ...assembly,
    layout_type_name: layoutTypeName,
  }));

  return {
    assemblies: assembliesWithLayoutName,
    totalPages: response.totalPages,
    totalCount: response.totalCount,
  };
};
